import { useTheme } from '@material-ui/core';
import CropFreeIcon from '@material-ui/icons/CropFree';
import GetAppIcon from '@material-ui/icons/GetApp';
import ViewModuleIcon from '@material-ui/icons/ViewModule';
import React, { useState } from 'react';
import { Button, useTranslate } from 'react-admin';

import { ConfigFileStatus } from '../../../entities/ConfigFileStatus';
import { useTerminalContext } from '../contexts/TerminalContext';
import { useCurrentTerminal } from '../hooks/useCurrentTerminal';
import { configApi } from '../services/configApi';
import type { Place } from '../types/warehouse.types';
import { formatDateTime } from '../utils/dateFormatters';
import { getPlaceTypeName } from '../utils/placeTypeHelpers';
import CodeDialog from './CodeDialog';
import { InfoField } from './InfoField';
import { valueTextStyle } from './styles';

interface PlaceInformationContentProps {
  place: Place;
}

export const PlaceInformationContent: React.FC<PlaceInformationContentProps> = ({ place }) => {
  const theme = useTheme();
  const translate = useTranslate();
  const { configFileStatus } = useTerminalContext();
  const terminal = useCurrentTerminal();
  const [openBarcode, setOpenBarcode] = useState(false);
  const dynamicTextStyle = valueTextStyle(theme);
  const hasCustomerName = Boolean(place.customer_name?.trim());
  const hasProductName = Boolean(place.product_name?.trim());

  const isProductionConfig = configFileStatus === ConfigFileStatus.production;
  const areSplittedPalletsProcessed = Boolean(terminal?.should_process_splitted_pallets);

  const handleDownloadSingleQrCode = async () => {
    try {
      await configApi.downloadSinglePlaceQrCode(place.id, place.address);
    } catch (error) {
      console.error('Failed to generate QR code:', error);
      alert(`Failed to generate QR code: ${error.message}`);
    }
  };

  const handleDownloadColumnQrCodes = async () => {
    try {
      await configApi.downloadColumnQrCodes(place.id);
    } catch (error) {
      console.error('Failed to generate QR codes:', error);
      alert(`Failed to generate QR codes: ${error.message}`);
    }
  };

  return (
    <>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          columnGap: '16px',
          alignItems: 'baseline',
        }}
      >
        <div style={{ minWidth: 0 }}>
          <InfoField label={translate('osfc.storage_ui.place_info.address')} value={place.address} valueStyle={{ ...dynamicTextStyle, fontWeight: 600 }} />
        </div>
        <div style={{ minWidth: 0, whiteSpace: 'nowrap' }}>
          <InfoField
            label={translate('osfc.storage_ui.place_info.status')}
            value={place.occupied ? translate('osfc.storage_ui.place_info.occupied') : place.blocked ? translate('osfc.storage_ui.place_info.blocked') : translate('osfc.storage_ui.place_info.empty')}
            valueStyle={{
              ...dynamicTextStyle,
              fontWeight: 600,
              color: place.occupied ? theme.palette.storageUi.errorText : theme.palette.storageUi.emptyTextColor,
            }}
          />
        </div>
      </div>
      
      {place.label && (
        <div style={{ marginTop: '8px' }}>
          <InfoField label={translate('osfc.storage_ui.place_info.label')} value={place.label} valueStyle={{ ...dynamicTextStyle, fontWeight: 600 }} />
        </div>
      )}

      {/* Two column layout for place details; virtual places show only relevant fields */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px', marginTop: '8px' }}>
        {place.isVirtual ? (
          <>
            <InfoField label={translate('osfc.storage_ui.place_info.capacity')} value={place.placeCapacity} valueStyle={dynamicTextStyle} />
          </>
        ) : (
          <>
            <InfoField label={translate('osfc.storage_ui.place_info.pallet_type')} value={getPlaceTypeName(place.place_type, translate)} valueStyle={dynamicTextStyle} />
            <InfoField label={translate('osfc.storage_ui.place_info.capacity')} value={place.placeCapacity} valueStyle={dynamicTextStyle} />
            <InfoField label={translate('osfc.storage_ui.place_info.max_weight')} value={place.placeMaxWeight} valueStyle={dynamicTextStyle} />
            <InfoField label={translate('osfc.storage_ui.place_info.area_temperature')} value={place.areaTemperature} valueStyle={dynamicTextStyle} />
            <InfoField label={translate('osfc.storage_ui.place_info.is_cross_dock')} value={place.isCrossDock ? translate('osfc.storage_ui.place_info.yes') : translate('osfc.storage_ui.place_info.no')} valueStyle={dynamicTextStyle} />
            <InfoField label={translate('osfc.storage_ui.place_info.is_preparation')} value={place.is_preparation ? translate('osfc.storage_ui.place_info.yes') : translate('osfc.storage_ui.place_info.no')} valueStyle={dynamicTextStyle} />
            <InfoField label={translate('osfc.storage_ui.place_info.is_external')} value={place.isExternal ? translate('osfc.storage_ui.place_info.yes') : translate('osfc.storage_ui.place_info.no')} valueStyle={dynamicTextStyle} />
            {areSplittedPalletsProcessed && (
              <InfoField label={translate('osfc.storage_ui.place_info.is_for_splitted_pallets')} value={place.is_for_splitted_pallets ? translate('osfc.storage_ui.place_info.yes') : translate('osfc.storage_ui.place_info.no')} valueStyle={dynamicTextStyle} />
            )}
          </>
        )}
      </div>
      <div style={{ marginTop: '8px' }}>
        <InfoField label={translate('osfc.storage_ui.place_info.updated')} value={formatDateTime(place.lastUpdated)} valueStyle={dynamicTextStyle} />
        {hasCustomerName && <InfoField label={translate('osfc.storage_ui.place_info.customer')} value={place.customer_name} valueStyle={dynamicTextStyle} />}
        {hasProductName && <InfoField label={translate('osfc.storage_ui.place_info.product')} value={place.product_name} valueStyle={dynamicTextStyle} />}

        {place.blocked && (
          <>
            <InfoField
              label={translate('osfc.storage_ui.place_info.lock_from')}
              value={formatDateTime(place.lock_datetime_from)}
              valueStyle={dynamicTextStyle}
            />
            <InfoField
              label={translate('osfc.storage_ui.place_info.lock_to')}
              value={formatDateTime(place.lock_datetime_to)}
              valueStyle={dynamicTextStyle}
            />
            {place.comment && (
              <InfoField
                label={translate('osfc.storage_ui.place_info.comment')}
                value={place.comment}
                valueStyle={dynamicTextStyle}
              />
            )}
          </>
        )}
      </div>

      {!place.isVirtual && (
        <div style={{ marginTop: '8px' }}>
          <Button onClick={() => setOpenBarcode(true)}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '12px' }}>
              <CropFreeIcon style={{ fontSize: '16px' }} />
              {translate('osfc.storage_ui.place_info.show_qr_code')}
            </div>
          </Button>
        </div>
      )}

      {isProductionConfig && !place.isVirtual && (
        <>
          <div style={{ marginTop: '6px' }}>
            <Button onClick={handleDownloadSingleQrCode}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '12px' }}>
                <GetAppIcon style={{ fontSize: '16px' }} />
                {translate('osfc.storage_ui.place_info.generate_qr_single')}
              </div>
            </Button>
          </div>

          <div style={{ marginTop: '6px' }}>
            <Button onClick={handleDownloadColumnQrCodes}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '12px' }}>
                <ViewModuleIcon style={{ fontSize: '16px' }} />
                {translate('osfc.storage_ui.place_info.generate_qr_column')}
              </div>
            </Button>
          </div>
        </>
      )}

      <CodeDialog
        open={openBarcode}
        onClose={() => setOpenBarcode(false)}
        value={place.address}
        type='qrcode'
        title={translate('osfc.storage_ui.place_info.place_address', { address: place.address })}
      />
    </>
  );
};
